import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function CNNFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [breakingNews, setBreakingNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [favorites, setFavorites] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(null);
  const [viewCounts, setViewCounts] = useState({});
  const [readLater, setReadLater] = useState([]);
  const [readingHistory, setReadingHistory] = useState([]);
  const [ratings, setRatings] = useState({});
  const [showRelated, setShowRelated] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [dateFilter, setDateFilter] = useState("all");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cnn_darkMode') === 'true');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('cnn_viewMode') || 'grid');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [onlyReadLater, setOnlyReadLater] = useState(false);
  const [cacheStatus, setCacheStatus] = useState('');
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState([]);
  const [newSinceLastVisit, setNewSinceLastVisit] = useState(0);
  const lastVisitRef = useRef(null);
  const searchInputRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [createData, setCreateData] = useState({
    headline: '',
    subheadline: '',
    text: '',
    category: 'politics',
    isBreaking: false,
    isLive: false,
    isOpinion: false,
    liveUrl: '',
    imageUrl: '',
    videoUrl: '',
  });
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const observerRef = useRef(null);

  const categories = [
    { id: "all", name: "Todas" },
    { id: "politics", name: "Política" },
    { id: "world", name: "Mundo" },
    { id: "business", name: "Negócios" },
    { id: "tech", name: "Tecnologia" },
    { id: "sports", name: "Esportes" },
    { id: "entertainment", name: "Entretenimento" },
    { id: "health", name: "Saúde" },
    { id: "science", name: "Ciência" },
  ];

  const getCacheKey = () => `cnn_cache_${category}_${sortBy}`;

  const getReadTime = (post) => {
    const text = post?.text || "";
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 180));
  };

  const savePreset = () => {
    if (!presetName.trim()) return;
    const preset = {
      id: Date.now(),
      name: presetName.trim(),
      values: { category, sortBy, minRating, dateFilter, onlyFavorites, onlyReadLater, viewMode },
    };
    const newPresets = [...presets, preset].slice(-8);
    setPresets(newPresets);
    localStorage.setItem('cnn_filterPresets', JSON.stringify(newPresets));
    setPresetName('');
  };

  const applyPreset = (preset) => {
    if (!preset?.values) return;
    setCategory(preset.values.category);
    setSortBy(preset.values.sortBy);
    setMinRating(preset.values.minRating);
    setDateFilter(preset.values.dateFilter);
    setOnlyFavorites(preset.values.onlyFavorites);
    setOnlyReadLater(preset.values.onlyReadLater);
    setViewMode(preset.values.viewMode);
  };

  const deletePreset = (id) => {
    const filtered = presets.filter((p) => p.id !== id);
    setPresets(filtered);
    localStorage.setItem('cnn_filterPresets', JSON.stringify(filtered));
  };

  const handleCreateChange = (field, value) => {
    setCreateData((prev) => ({ ...prev, [field]: value }));
  };

  const uploadImageFile = async (file) => {
    if (!file) return null;
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('files', file);
      formData.append('path', 'cnn/images');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/uploadImages`,
        formData,
        { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' } }
      );
      return data?.[0]?.url || null;
    } catch (err) {
      console.error('Erro ao fazer upload de imagem:', err);
      setCreateError('Falha ao enviar imagem.');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const uploadVideoFile = async (file) => {
    if (!file) return null;
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('files', file);
      formData.append('path', 'cnn/videos');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/uploadVideos`,
        formData,
        { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' } }
      );
      return data?.[0]?.url || null;
    } catch (err) {
      console.error('Erro ao fazer upload de vídeo:', err);
      setCreateError('Falha ao enviar vídeo.');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const createCnnPost = async () => {
    setCreateError('');
    if (!createData.headline.trim() || !createData.text.trim()) {
      setCreateError('Título e conteúdo são obrigatórios.');
      return;
    }
    try {
      setCreateLoading(true);
      let finalImageUrl = createData.imageUrl;
      let finalVideoUrl = createData.videoUrl;
      if (fileInputRef.current?.files?.[0]) {
        finalImageUrl = await uploadImageFile(fileInputRef.current.files[0]);
        if (!finalImageUrl) return;
      }
      if (videoInputRef.current?.files?.[0]) {
        finalVideoUrl = await uploadVideoFile(videoInputRef.current.files[0]);
        if (!finalVideoUrl) return;
      }
      const payload = {
        editorialType: 'cnn',
        text: createData.text,
        images: finalImageUrl ? [finalImageUrl] : undefined,
        videos: finalVideoUrl ? [finalVideoUrl] : undefined,
        cnnData: {
          headline: createData.headline,
          subheadline: createData.subheadline,
          category: createData.category,
          isBreaking: createData.isBreaking,
          isLive: createData.isLive,
          isOpinion: createData.isOpinion,
          liveUrl: createData.liveUrl,
        },
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/create`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const created = data.post || data;
      if (created?.cnnData?.isBreaking) {
        setBreakingNews((prev) => [created, ...prev]);
      }
      setPosts((prev) => [created, ...prev]);
      setShowCreateModal(false);
      setCreateData({
        headline: '',
        subheadline: '',
        text: '',
        category: 'politics',
        isBreaking: false,
        isLive: false,
        isOpinion: false,
        liveUrl: '',
        imageUrl: '',
        videoUrl: '',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (videoInputRef.current) videoInputRef.current.value = '';
    } catch (err) {
      setCreateError('Erro ao publicar. Tente novamente.');
      console.error('Erro ao criar notícia CNN:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditPost = (post) => {
    setSelectedArticle(null);
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedArticle(null);
  };

  const fetchPosts = async (pageNum = 1) => {
    try {
      setLoading(true);
      const categoryParam = category !== "all" ? `&category=${category}` : "";
      const sortParam = `&sort=${sortBy}`;
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/cnn?page=${pageNum}&limit=20${categoryParam}${sortParam}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const allPosts = data.posts || [];
      const breaking = allPosts.filter((p) => p.cnnData?.isBreaking);
      const regular = allPosts.filter((p) => !p.cnnData?.isBreaking);

      if (pageNum === 1) {
        setBreakingNews(breaking);
        setPosts(regular);
      } else {
        setPosts((prev) => [...prev, ...regular]);
      }
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      localStorage.setItem(getCacheKey(), JSON.stringify({ breaking, regular, pagination, ts: Date.now() }));
      setCacheStatus('Atualizado agora');
      if (pageNum === 1) {
        const last = lastVisitRef.current;
        if (last) {
          setNewSinceLastVisit(regular.filter((p) => new Date(p.createdAt) > last).length);
        } else {
          setNewSinceLastVisit(0);
        }
        const now = new Date();
        lastVisitRef.current = now;
        localStorage.setItem('cnn_lastVisit', now.toISOString());
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching CNN feed:", error);
      setCacheStatus('Falha ao atualizar');
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem(getCacheKey());
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setPosts(parsed.regular || []);
        setBreakingNews(parsed.breaking || []);
        setHasMore((parsed.pagination?.page || 1) < (parsed.pagination?.pages || 1));
        setCacheStatus('Mostrando conteúdo salvo');
        setLoading(false);
      } catch (err) {
        console.error('Erro ao ler cache CNN:', err);
      }
    }
    setPage(1);
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sortBy]);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('cnn_favorites');
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
    const savedReadLater = localStorage.getItem('cnn_readLater');
    if (savedReadLater) setReadLater(JSON.parse(savedReadLater));
    const savedHistory = localStorage.getItem('cnn_readingHistory');
    if (savedHistory) setReadingHistory(JSON.parse(savedHistory));
    const savedRatings = localStorage.getItem('cnn_ratings');
    if (savedRatings) setRatings(JSON.parse(savedRatings));
    const savedPresets = localStorage.getItem('cnn_filterPresets');
    if (savedPresets) setPresets(JSON.parse(savedPresets));
    const savedVisit = localStorage.getItem('cnn_lastVisit');
    lastVisitRef.current = savedVisit ? new Date(savedVisit) : null;
  }, []);

  useEffect(() => {
    localStorage.setItem('cnn_darkMode', darkMode);
    if (darkMode) {
      document.body.classList.add('cnn_dark_mode');
    } else {
      document.body.classList.remove('cnn_dark_mode');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('cnn_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting) {
        loadMore();
      }
    }, { rootMargin: '240px' });
    const node = observerRef.current;
    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, page]);

  useEffect(() => {
    const handleShortcut = (e) => {
      const target = e.target;
      const isTyping = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTyping) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key.toLowerCase() === 'f') setOnlyFavorites((prev) => !prev);
      if (e.key.toLowerCase() === 'l') setOnlyReadLater((prev) => !prev);
      if (e.key.toLowerCase() === 'g') setViewMode('grid');
      if (e.key.toLowerCase() === 't') setViewMode('list');
      if (e.key.toLowerCase() === 'd') setDarkMode((prev) => !prev);
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const headline = post.cnnData?.headline || post.text || '';
    const subheadline = post.cnnData?.subheadline || '';
    return headline.toLowerCase().includes(searchLower) || subheadline.toLowerCase().includes(searchLower);
  });

  const getFilteredByDate = (posts) => {
    const now = new Date();
    switch(dateFilter) {
      case 'today':
        return posts.filter(p => new Date(p.createdAt).toDateString() === now.toDateString());
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return posts.filter(p => new Date(p.createdAt) >= weekAgo);
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return posts.filter(p => new Date(p.createdAt) >= monthAgo);
      default:
        return posts;
    }
  };

  let displayPosts = getFilteredByDate(filteredPosts);
  if (minRating > 0) displayPosts = displayPosts.filter(p => (ratings[p._id] || 0) >= minRating);
  if (onlyFavorites) displayPosts = displayPosts.filter(p => favorites.includes(p._id));
  if (onlyReadLater) displayPosts = displayPosts.filter(p => readLater.includes(p._id));

  const toggleFavorite = (postId, e) => {
    e.stopPropagation();
    const newFavorites = favorites.includes(postId)
      ? favorites.filter(id => id !== postId)
      : [...favorites, postId];
    setFavorites(newFavorites);
    localStorage.setItem('cnn_favorites', JSON.stringify(newFavorites));
  };

  const incrementViewCount = async (postId) => {
    if (viewCounts[postId]) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/posts/${postId}/view`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setViewCounts(prev => ({ ...prev, [postId]: true }));
    } catch (error) {
      console.error('Error incrementing view:', error);
    }
  };

  const addToReadingHistory = (post) => {
    const historyEntry = {
      postId: post._id,
      title: post.cnnData?.headline || post.text,
      readAt: new Date().toISOString(),
      category: post.cnnData?.category,
    };
    const newHistory = [historyEntry, ...readingHistory.filter(h => h.postId !== post._id)].slice(0, 50);
    setReadingHistory(newHistory);
    localStorage.setItem('cnn_readingHistory', JSON.stringify(newHistory));
  };

  const openArticle = (post) => {
    setSelectedArticle(post);
    incrementViewCount(post._id);
    addToReadingHistory(post);
  };

  const toggleReadLater = (postId, e) => {
    e.stopPropagation();
    const newReadLater = readLater.includes(postId)
      ? readLater.filter(id => id !== postId)
      : [...readLater, postId];
    setReadLater(newReadLater);
    localStorage.setItem('cnn_readLater', JSON.stringify(newReadLater));
  };

  const rateArticle = (postId, rating) => {
    const newRatings = { ...ratings, [postId]: rating };
    setRatings(newRatings);
    localStorage.setItem('cnn_ratings', JSON.stringify(newRatings));
  };

  const getRelatedArticles = (post) => {
    const category = post.cnnData?.category;
    if (!category) return [];
    return filteredPosts.filter(p => p._id !== post._id && p.cnnData?.category === category).slice(0, 3);
  };

  const exportToPDF = (post) => {
    const content = `${post.cnnData?.headline || post.text}\n\n${post.cnnData?.subheadline || ''}\n\nPor: ${post.cnnData?.author || `${post.user.first_name} ${post.user.last_name}`}\nData: ${new Date(post.createdAt).toLocaleString('pt-BR')}\n\n${post.text}\n\n---\nCompartilhado via CNN Feed - ${window.location.href}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${post.cnnData?.headline || 'artigo'}.txt`;
    a.click();
  };

  const shareArticle = async (post, platform) => {
    const url = `${window.location.origin}/article/${post._id}`;
    const text = post.cnnData?.headline || post.text;
    
    switch(platform) {
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copiado!');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      default:
        break;
    }
    setShowShareMenu(null);
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      politics: "#cc0000", world: "#0077b5", business: "#10b981", tech: "#6366f1",
      sports: "#f59e0b", entertainment: "#ec4899", health: "#14b8a6", science: "#8b5cf6",
    };
    return colors[cat] || "#cc0000";
  };

  const liveNews = [...breakingNews, ...posts].filter((p) => p?.cnnData?.isLive);

  if (loading && posts.length === 0) {
    return <div className="cnn_feed_loading"><div className="cnn_loader"></div><span>Carregando notícias...</span></div>;
  }

  return (
    <div className="cnn_feed">
      <div className="cnn_search_bar">
        <svg viewBox="0 0 24 24" fill="currentColor" className="cnn_search_icon"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
        <input ref={searchInputRef} type="text" placeholder="Buscar notícias..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="cnn_search_input" />
        {searchQuery && <button onClick={() => setSearchQuery("")} className="cnn_search_clear">✕</button>}
      </div>

      <div className="cnn_sort_bar">
        <div className="cnn_sort_buttons">
          <button className={`cnn_sort_btn ${sortBy === "recent" ? "active" : ""}`} onClick={() => setSortBy("recent")}>📅 Mais Recentes</button>
          <button className={`cnn_sort_btn ${sortBy === "popular" ? "active" : ""}`} onClick={() => setSortBy("popular")}>🔥 Mais Populares</button>
          <button className={`cnn_sort_btn ${sortBy === "trending" ? "active" : ""}`} onClick={() => setSortBy("trending")}>📈 Em Alta</button>
          <button className="cnn_sort_btn" onClick={() => setShowFilters(!showFilters)}>⚙️ Filtros</button>
          <button className="cnn_sort_btn" onClick={() => setDarkMode(!darkMode)}>{darkMode ? "☀️" : "🌙"}</button>
          <button className="cnn_sort_btn" onClick={() => setFocusMode(!focusMode)}>{focusMode ? "👁️" : "📖"}</button>
          <button className="cnn_sort_btn" onClick={() => setShowRelated(!showRelated)}>⏱️ Histórico</button>
          <button className={`cnn_sort_btn ${onlyFavorites ? "active" : ""}`} onClick={() => setOnlyFavorites((prev) => !prev)}>❤️ Favoritos</button>
          <button className={`cnn_sort_btn ${onlyReadLater ? "active" : ""}`} onClick={() => setOnlyReadLater((prev) => !prev)}>⏳ Ler Depois</button>
          <div className="cnn_view_toggle">
            <button className={`cnn_sort_btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>🗂️ Grade</button>
            <button className={`cnn_sort_btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>📃 Lista</button>
          </div>
          <button className="cnn_sort_btn cnn_primary" onClick={() => setShowCreateModal(true)}>➕ Publicar</button>
        </div>

        {showFilters && (
          <div className="cnn_advanced_filters">
            <div className="cnn_filter_group">
              <label>Período:</label>
              <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                <option value="all">Todos</option>
                <option value="today">Hoje</option>
                <option value="week">Última Semana</option>
                <option value="month">Último Mês</option>
              </select>
            </div>
            <div className="cnn_filter_group">
              <label>Avaliação:</label>
              <div className="cnn_rating_filter">
                {[0, 1, 2, 3, 4, 5].map(r => (
                  <button key={r} className={`cnn_rating_btn ${minRating === r ? 'active' : ''}`} onClick={() => setMinRating(r)}>
                    {r === 0 ? 'Sem' : '⭐'.repeat(r)}
                  </button>
                ))}
              </div>
            </div>
            <div className="cnn_filter_group">
              <label>Ler Depois: {readLater.length}</label>
              {readLater.length > 0 && (
                <button className="cnn_view_readlater" onClick={() => { const p = posts.find(x => readLater.includes(x._id)); if (p) setSelectedArticle(p); }}>Ver</button>
              )}
            </div>
            <div className="cnn_filter_group cnn_filter_presets">
              <label>Presets rápidos</label>
              <div className="cnn_preset_save">
                <input type="text" value={presetName} placeholder="Nome do preset" onChange={(e) => setPresetName(e.target.value)} />
                <button onClick={savePreset}>Salvar</button>
              </div>
              {presets.length > 0 && (
                <div className="cnn_preset_list">
                  {presets.map((p) => (
                    <div key={p.id} className="cnn_preset_item">
                      <button onClick={() => applyPreset(p)}>{p.name}</button>
                      <span onClick={() => deletePreset(p.id)}>✕</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

        <div className="cnn_quick_insights">
          <span>❤️ {favorites.length} fav</span>
          <span>⏳ {readLater.length} ler depois</span>
          <span>📖 {readingHistory.length} lidas</span>
        {newSinceLastVisit > 0 && <span>🆕 {newSinceLastVisit} novas</span>}
          {cacheStatus && <span className="cnn_cache_status">{cacheStatus}</span>}
        </div>

        {liveNews.length > 0 && (
          <div className="cnn_live_bar" onClick={() => openArticle(liveNews[0])}>
            <span className="cnn_live_pill">AO VIVO</span>
            <div className="cnn_live_scroll">
              {liveNews.map((live, i) => (
                <span key={i} className="cnn_live_item">{live.cnnData?.headline || live.text}</span>
              ))}
            </div>
            <button className="cnn_live_cta">Assistir agora</button>
          </div>
        )}

      {breakingNews.length > 0 && (
        <div className="cnn_breaking_banner">
          <span className="cnn_breaking_label">BREAKING NEWS</span>
          <div className="cnn_breaking_scroll">
            {breakingNews.map((news, i) => (
              <span key={i} className="cnn_breaking_item">{news.cnnData?.headline || news.text}</span>
            ))}
          </div>
        </div>
      )}

      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button key={filter.id} className={`filter_btn ${category === filter.id ? "active" : ""}`} onClick={() => { setCategory(filter.id); setPage(1); }}>
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {showRelated && readingHistory.length > 0 && (
        <div className="cnn_reading_history">
          <div className="cnn_history_header">
            <h3>📖 Histórico</h3>
            <button onClick={() => { setReadingHistory([]); localStorage.removeItem('cnn_readingHistory'); setShowRelated(false); }} className="cnn_clear_history">Limpar</button>
          </div>
          <div className="cnn_history_items">
            {readingHistory.slice(0, 5).map((item, idx) => (
              <div key={idx} className="cnn_history_item">
                <span className="cnn_history_title">{item.title}</span>
                <span className="cnn_history_time"><Moment fromNow locale="pt-br">{item.readAt}</Moment></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchQuery && <div className="cnn_search_results">{displayPosts.length} resultado{displayPosts.length !== 1 ? 's' : ''}</div>}

      {displayPosts.length === 0 ? (
        <div className="cnn_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
          <h3>Nenhuma notícia encontrada</h3>
          <p>{searchQuery ? 'Tente outras palavras' : 'Seja o primeiro a publicar!'}</p>
        </div>
      ) : (
        <>
          {displayPosts[0] && (
            <div className={`cnn_featured ${focusMode ? 'cnn_focus_mode' : ''}`} onClick={() => openArticle(displayPosts[0])}>
              <div className="cnn_featured_image">
                {displayPosts[0].images?.[0] && <img src={displayPosts[0].images[0]?.url || displayPosts[0].images[0]} alt="" />}
                <div className="cnn_featured_overlay">
                  {displayPosts[0].cnnData?.category && <span className="cnn_category_tag" style={{ background: getCategoryColor(displayPosts[0].cnnData.category) }}>{categories.find((c) => c.id === displayPosts[0].cnnData.category)?.name}</span>}
                  <h1 className="cnn_featured_headline">{displayPosts[0].cnnData?.headline || displayPosts[0].text}</h1>
                  {displayPosts[0].cnnData?.subheadline && <p className="cnn_featured_sub">{displayPosts[0].cnnData.subheadline}</p>}
                  <div className="cnn_featured_meta">
                    <span>{displayPosts[0].cnnData?.author || `${displayPosts[0].user.first_name} ${displayPosts[0].user.last_name}`}</span>
                    <span>•</span>
                    <Moment fromNow locale="pt-br">{displayPosts[0].createdAt}</Moment>
                  </div>
                </div>
                <div className="cnn_featured_actions">
                  <button className="cnn_favorite_featured" onClick={(e) => toggleFavorite(displayPosts[0]._id, e)}>{favorites.includes(displayPosts[0]._id) ? "❤️" : "🤍"}</button>
                  <button className="cnn_readlater_featured" onClick={(e) => toggleReadLater(displayPosts[0]._id, e)}>{readLater.includes(displayPosts[0]._id) ? "⏱️" : "⏳"}</button>
                </div>
              </div>
            </div>
          )}

          <div className={`cnn_grid ${viewMode === 'list' ? 'cnn_list_mode' : ''}`}>
            {displayPosts.slice(1).map((post) => {
              const isRead = readingHistory.some((h) => h.postId === post._id);
              return (
              <article key={post._id} className="cnn_article" onClick={() => openArticle(post)}>
                <div className="cnn_article_image">
                  {post.images?.[0] ? (
                    <img src={post.images[0]?.url || post.images[0]} alt="" />
                  ) : (
                    <div className="cnn_no_image"><svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg></div>
                  )}
                  {post.cnnData?.isOpinion && <span className="cnn_opinion_tag">Opinião</span>}
                  {post.cnnData?.isLive && <span className="cnn_live_tag">AO VIVO</span>}
                  <div className="cnn_article_card_buttons">
                    <button className="cnn_favorite_btn" onClick={(e) => toggleFavorite(post._id, e)}>{favorites.includes(post._id) ? "❤️" : "🤍"}</button>
                    <button className="cnn_readlater_btn" onClick={(e) => toggleReadLater(post._id, e)}>{readLater.includes(post._id) ? "⏱️" : "⏳"}</button>
                  </div>
                </div>
                <div className="cnn_article_content">
                  {post.cnnData?.category && <span className="cnn_category_small" style={{ color: getCategoryColor(post.cnnData.category) }}>{categories.find((c) => c.id === post.cnnData.category)?.name}</span>}
                  <h2 className="cnn_article_headline">{post.cnnData?.headline || post.text}</h2>
                  <div className="cnn_article_meta">
                    <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                    <span className="cnn_article_meta_item">{getReadTime(post)} min</span>
                    <span className={`cnn_article_meta_item ${isRead ? 'cnn_read_flag' : ''}`}>{isRead ? 'Lido' : 'Novo'}</span>
                    <div className="cnn_article_actions">
                      <button className="cnn_share_btn" onClick={(e) => { e.stopPropagation(); setShowShareMenu(showShareMenu === post._id ? null : post._id); }}>📤</button>
                      {showShareMenu === post._id && (
                        <div className="cnn_share_menu">
                          <button onClick={() => shareArticle(post, 'copy')}>Copiar Link</button>
                          <button onClick={() => shareArticle(post, 'whatsapp')}>WhatsApp</button>
                          <button onClick={() => shareArticle(post, 'twitter')}>Twitter</button>
                          <button onClick={() => shareArticle(post, 'facebook')}>Facebook</button>
                        </div>
                      )}
                      <div onClick={(e) => e.stopPropagation()}>
                        <FeedPostMenu post={post} onEdit={handleEditPost} onPostDeleted={handlePostDeleted} />
                      </div>
                    </div>
                  </div>
                </div>
              </article>
              );
            })}
          </div>

          {hasMore && (
            <div className="cnn_load_more">
              <button onClick={loadMore} disabled={loading}>{loading ? "Carregando..." : "Carregar mais notícias"}</button>
              <div ref={observerRef} className="cnn_infinite_sentinel"></div>
            </div>
          )}
        </>
      )}

      {selectedArticle && (
        <div className={`cnn_modal_overlay ${focusMode ? 'cnn_focus_mode' : ''}`} onClick={() => setSelectedArticle(null)}>
          <div className={`cnn_modal ${focusMode ? 'cnn_modal_focus' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button className="cnn_modal_close" onClick={() => setSelectedArticle(null)}>✕</button>
            
            <div className="cnn_modal_header_actions">
              <button className="cnn_modal_header_btn" onClick={(e) => toggleReadLater(selectedArticle._id, e)}>{readLater.includes(selectedArticle._id) ? "⏱️" : "⏳"}</button>
              <button className="cnn_modal_header_btn" onClick={() => exportToPDF(selectedArticle)}>📄</button>
            </div>

            {selectedArticle.images?.[0] && (
              <div className="cnn_modal_image">
                <img src={selectedArticle.images[0]?.url || selectedArticle.images[0]} alt="" />
              </div>
            )}
            <div className="cnn_modal_content">
              {selectedArticle.cnnData?.category && (
                <span className="cnn_category_tag" style={{ background: getCategoryColor(selectedArticle.cnnData.category) }}>
                  {categories.find((c) => c.id === selectedArticle.cnnData.category)?.name}
                </span>
              )}
              {selectedArticle.cnnData?.isLive && (
                <span className="cnn_live_tag_inline">AO VIVO</span>
              )}
              <h1>{selectedArticle.cnnData?.headline || selectedArticle.text}</h1>
              {selectedArticle.cnnData?.subheadline && <p className="cnn_modal_subheadline">{selectedArticle.cnnData.subheadline}</p>}
              <div className="cnn_modal_author">
                <Link to={`/profile/${selectedArticle.user.username}`}>
                  <img src={selectedArticle.user.picture} alt="" />
                </Link>
                <div>
                  <span className="cnn_author_name">{selectedArticle.cnnData?.author || `${selectedArticle.user.first_name} ${selectedArticle.user.last_name}`}</span>
                  <Moment fromNow locale="pt-br">{selectedArticle.createdAt}</Moment>
                </div>
              </div>
              <div className="cnn_modal_body">
                <p>{selectedArticle.text}</p>
              </div>

              <div className="cnn_modal_rating">
                <span className="cnn_rating_label">Como você avalia este artigo?</span>
                <div className="cnn_rating_stars">
                  {[1, 2, 3, 4, 5].map(r => (
                    <button key={r} className={`cnn_star ${ratings[selectedArticle._id] >= r ? 'active' : ''}`} onClick={() => rateArticle(selectedArticle._id, r)}>⭐</button>
                  ))}
                </div>
              </div>

              {getRelatedArticles(selectedArticle).length > 0 && (
                <div className="cnn_related_articles">
                  <h3>📰 Artigos Relacionados</h3>
                  <div className="cnn_related_list">
                    {getRelatedArticles(selectedArticle).map(related => (
                      <button key={related._id} className="cnn_related_item" onClick={() => openArticle(related)}>
                        <span>{related.cnnData?.headline || related.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="cnn_modal_actions">
                <button className="cnn_modal_favorite" onClick={(e) => toggleFavorite(selectedArticle._id, e)}>
                  {favorites.includes(selectedArticle._id) ? "❤️ Remover" : "🤍 Adicionar aos Favoritos"}
                </button>
                {selectedArticle.cnnData?.liveUrl && (
                  <a className="cnn_live_cta_link" href={selectedArticle.cnnData.liveUrl} target="_blank" rel="noreferrer">Assistir transmissão</a>
                )}
                <div className="cnn_modal_share">
                  <button className="cnn_modal_share_btn" onClick={() => setShowShareMenu(showShareMenu === 'modal' ? null : 'modal')}>📤 Compartilhar</button>
                  {showShareMenu === 'modal' && (
                    <div className="cnn_share_menu_modal">
                      <button onClick={() => shareArticle(selectedArticle, 'copy')}>Copiar Link</button>
                      <button onClick={() => shareArticle(selectedArticle, 'whatsapp')}>WhatsApp</button>
                      <button onClick={() => shareArticle(selectedArticle, 'twitter')}>Twitter</button>
                      <button onClick={() => shareArticle(selectedArticle, 'facebook')}>Facebook</button>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedArticle.cnnData?.sources?.length > 0 && (
                <div className="cnn_modal_sources">
                  <h4>Fontes</h4>
                  <ul>
                    {selectedArticle.cnnData.sources.map((source, i) => (
                      <li key={i}>{source}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={() => setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {showCreateModal && (
        <div className="cnn_modal_overlay" onClick={() => setShowCreateModal(false)}>
          <div className="cnn_modal cnn_create_modal" onClick={(e) => e.stopPropagation()}>
            <button className="cnn_modal_close" onClick={() => setShowCreateModal(false)}>✕</button>
            <h2>Publicar notícia CNN</h2>
            <div className="cnn_form_grid">
              <label>
                Título
                <input type="text" value={createData.headline} onChange={(e) => handleCreateChange('headline', e.target.value)} />
              </label>
              <label>
                Subtítulo
                <input type="text" value={createData.subheadline} onChange={(e) => handleCreateChange('subheadline', e.target.value)} />
              </label>
              <label>
                Categoria
                <select value={createData.category} onChange={(e) => handleCreateChange('category', e.target.value)}>
                  {categories.filter(c => c.id !== 'all').map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
              <label className="cnn_checkbox">
                <input type="checkbox" checked={createData.isBreaking} onChange={(e) => handleCreateChange('isBreaking', e.target.checked)} /> Breaking News
              </label>
              <label className="cnn_checkbox">
                <input type="checkbox" checked={createData.isLive} onChange={(e) => handleCreateChange('isLive', e.target.checked)} /> Ao vivo
              </label>
              <label className="cnn_checkbox">
                <input type="checkbox" checked={createData.isOpinion} onChange={(e) => handleCreateChange('isOpinion', e.target.checked)} /> Opinião
              </label>
              <label>
                URL da transmissão (opcional)
                <input type="text" value={createData.liveUrl} onChange={(e) => handleCreateChange('liveUrl', e.target.value)} placeholder="https://" />
              </label>
            </div>
            <div className="cnn_file_upload">
              <label>Imagem/Capa (arquivo):</label>
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*" 
                disabled={uploadingFile}
              />
              {fileInputRef.current?.files?.[0] && <span className="cnn_file_ok">✓ {fileInputRef.current.files[0].name}</span>}
            </div>
            <div className="cnn_file_upload">
              <label>Vídeo (arquivo, opcional):</label>
              <input 
                ref={videoInputRef}
                type="file" 
                accept="video/*" 
                disabled={uploadingFile}
              />
              {videoInputRef.current?.files?.[0] && <span className="cnn_file_ok">✓ {videoInputRef.current.files[0].name}</span>}
            </div>
            <div className="cnn_form_grid">
              <label>
                Conteúdo
                <textarea rows="6" value={createData.text} onChange={(e) => handleCreateChange('text', e.target.value)}></textarea>
              </label>
            </div>
            {createError && <div className="cnn_error_text">{createError}</div>}
            {uploadingFile && <div className="cnn_upload_status">Enviando arquivo...</div>}
            <div className="cnn_create_actions">
              <button onClick={() => setShowCreateModal(false)} className="cnn_secondary_btn" disabled={uploadingFile || createLoading}>Cancelar</button>
              <button onClick={createCnnPost} disabled={createLoading || uploadingFile} className="cnn_primary_btn">{uploadingFile ? 'Enviando...' : createLoading ? 'Publicando...' : 'Publicar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
